import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import {
  TrendingUp, TrendingDown, Minus, Shield, AlertTriangle,
  CheckCircle2, Target, Clock, Brain, ArrowLeft, ArrowRight,
  BarChart3, Activity, Zap, Lock, Sparkles, ChevronRight
} from "lucide-react";

const ProgressReportPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);

  const isPro = user?.role === "PRO";

  useEffect(() => {
    fetchProgressReport();
  }, []);

  const fetchProgressReport = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await managedAxios.get(`${API_BASE}/progress-report/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(response.data);
    } catch (error) {
      console.error("Error fetching progress report:", error);
      toast({
        variant: "destructive",
        description: "Failed to load progress report"
      });
    } finally {
      setLoading(false);
    }
  };

  const getSafetyZoneColor = (status) => {
    switch (status) {
      case "safe": return "from-emerald-500 to-green-500";
      case "borderline": return "from-amber-500 to-orange-500";
      case "unsafe": return "from-red-500 to-rose-500";
      default: return "from-slate-500 to-slate-600";
    }
  };

  const getSafetyZoneIcon = (status) => {
    switch (status) {
      case "safe": return <CheckCircle2 className="w-6 h-6" />;
      case "borderline": return <AlertTriangle className="w-6 h-6" />;
      case "unsafe": return <Shield className="w-6 h-6" />;
      default: return <Target className="w-6 h-6" />;
    }
  };

  const getMovementIcon = (movement) => {
    switch (movement) {
      case "improving": return <TrendingUp className="w-4 h-4 text-emerald-500" />;
      case "declining": return <TrendingDown className="w-4 h-4 text-red-500" />;
      default: return <Minus className="w-4 h-4 text-slate-500" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/30 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-slate-600">Loading your progress report...</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-slate-50/30 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center space-y-4">
            <Target className="w-12 h-12 text-slate-400 mx-auto" />
            <h2 className="text-xl font-bold text-slate-900">No Data Yet</h2>
            <p className="text-slate-600">Take some tests to see your progress report.</p>
            <Button onClick={() => navigate("/prelims")} className="bg-indigo-500 hover:bg-indigo-600">
              Start Practicing
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/30">
      {/* Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-20 w-72 h-72 bg-indigo-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-purple-100/40 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-6 py-10 space-y-8">
        {/* Back Button */}
        <Button variant="outline" onClick={() => navigate('/dashboard')} className="mb-2">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        {/* Header */}
        <div className="glass-card rounded-2xl p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <Badge className={`px-3 py-1 ${isPro ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white border-0' : 'bg-slate-100 text-slate-600 border-0'}`}>
                  {isPro ? "PRO" : "FREE"}
                </Badge>
                <Badge variant="outline" className="border-indigo-200">
                  {report.total_tests_taken} Tests Taken
                </Badge>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
                Behaviour-Based Progress Report
              </h1>
              <p className="text-slate-600">
                Tracks how close you are to clearing UPSC — not just marks
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500">Last Updated</p>
              <p className="text-lg font-semibold text-slate-900">
                {report.latest_snapshot?.test_date?.split("T")[0] || "N/A"}
              </p>
            </div>
          </div>
        </div>

        {/* Headline Card */}
        <div className={`rounded-2xl p-6 bg-gradient-to-r ${getSafetyZoneColor(report.current_safety_zone?.status)} text-white shadow-lg`}>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
              {getSafetyZoneIcon(report.current_safety_zone?.status)}
            </div>
            <div>
              <h2 className="text-xl font-bold">{report.headline}</h2>
              <p className="text-white/90">{report.insight}</p>
            </div>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {/* Safety Zone Card */}
          <Card className="border-0 shadow-sm overflow-hidden">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-900">Prelims Safety Zone</h3>
                <Badge className={`bg-gradient-to-r ${getSafetyZoneColor(report.current_safety_zone?.status)} text-white border-0`}>
                  {report.current_safety_zone?.status?.toUpperCase()}
                </Badge>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Score</span>
                  <span className="font-semibold">{report.current_safety_zone?.score}%</span>
                </div>
                <Progress 
                  value={report.current_safety_zone?.score || 0} 
                  className="h-3"
                />
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Cutoff Distance</span>
                <span className={`font-semibold ${report.current_safety_zone?.cutoff_distance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {report.current_safety_zone?.cutoff_distance > 0 ? '+' : ''}{report.current_safety_zone?.cutoff_distance}%
                </span>
              </div>

              {isPro && report.current_safety_zone?.movement && (
                <div className="flex items-center gap-2 pt-2 border-t">
                  {getMovementIcon(report.current_safety_zone.movement)}
                  <span className="text-sm capitalize">{report.current_safety_zone.movement}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* CSAT Safety Card */}
          <Card className="border-0 shadow-sm overflow-hidden">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-900">CSAT Safety</h3>
                <Badge className={`bg-gradient-to-r ${getSafetyZoneColor(report.csat_safety?.status)} text-white border-0`}>
                  {report.csat_safety?.status?.toUpperCase()}
                </Badge>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Qualifying Probability</span>
                  <span className="font-semibold">{report.csat_safety?.qualifying_probability}%</span>
                </div>
                <Progress 
                  value={report.csat_safety?.qualifying_probability || 0} 
                  className="h-3"
                />
              </div>

              {report.csat_safety?.weak_areas?.length > 0 && (
                <div className="pt-2 border-t">
                  <p className="text-xs text-slate-500 mb-2">Focus Areas:</p>
                  <div className="flex flex-wrap gap-1">
                    {report.csat_safety.weak_areas.map((area, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {area}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Latest Snapshot Card */}
          <Card className="border-0 shadow-sm overflow-hidden">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-900">Latest Test</h3>
                <Badge variant="outline">{report.latest_snapshot?.test_type}</Badge>
              </div>
              
              {report.latest_snapshot ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="p-2 bg-emerald-50 rounded-lg">
                      <p className="text-lg font-bold text-emerald-600">{report.latest_snapshot.correct}</p>
                      <p className="text-xs text-slate-500">Correct</p>
                    </div>
                    <div className="p-2 bg-red-50 rounded-lg">
                      <p className="text-lg font-bold text-red-600">{report.latest_snapshot.incorrect}</p>
                      <p className="text-xs text-slate-500">Incorrect</p>
                    </div>
                    <div className="p-2 bg-slate-50 rounded-lg">
                      <p className="text-lg font-bold text-slate-600">{report.latest_snapshot.attempted}</p>
                      <p className="text-xs text-slate-500">Attempted</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm pt-2 border-t">
                    <span className="text-slate-500">Accuracy</span>
                    <span className="font-semibold">{report.latest_snapshot.accuracy?.toFixed(1)}%</span>
                  </div>
                  
                  {report.latest_snapshot.panic_detected && (
                    <div className="flex items-center gap-2 text-amber-600 text-sm">
                      <AlertTriangle className="w-4 h-4" />
                      <span>Panic pattern detected</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-slate-500 text-sm">No tests taken yet</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* PRO Features Section */}
        {isPro ? (
          <>
            {/* Behaviour Metrics */}
            {report.behaviour_metrics && (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                    <Brain className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Behaviour Analysis</h3>
                    <p className="text-sm text-slate-500">How you behave in exams, not just marks</p>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                  {/* Panic Index */}
                  <div className="p-4 rounded-xl bg-slate-50 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Panic Index</span>
                      <AlertTriangle className={`w-4 h-4 ${report.behaviour_metrics.panic_index > 60 ? 'text-red-500' : 'text-emerald-500'}`} />
                    </div>
                    <p className="text-2xl font-bold">{report.behaviour_metrics.panic_index}%</p>
                    <p className="text-xs text-slate-500">
                      {report.behaviour_metrics.panic_index > 60 ? 'High - needs control' : 'Under control'}
                    </p>
                  </div>

                  {/* Over-Attempt Rate */}
                  <div className="p-4 rounded-xl bg-slate-50 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Over-Attempt</span>
                      <Activity className={`w-4 h-4 ${report.behaviour_metrics.over_attempt_rate > 30 ? 'text-amber-500' : 'text-emerald-500'}`} />
                    </div>
                    <p className="text-2xl font-bold">{report.behaviour_metrics.over_attempt_rate}%</p>
                    <p className="text-xs text-slate-500">
                      {report.behaviour_metrics.over_attempt_rate > 30 ? 'Attempting too many' : 'Good discipline'}
                    </p>
                  </div>

                  {/* Time Leak */}
                  <div className="p-4 rounded-xl bg-slate-50 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Time Leak</span>
                      <Clock className={`w-4 h-4 ${report.behaviour_metrics.time_leak_rate > 40 ? 'text-red-500' : 'text-emerald-500'}`} />
                    </div>
                    <p className="text-2xl font-bold">{report.behaviour_metrics.time_leak_rate}%</p>
                    <p className="text-xs text-slate-500">
                      {report.behaviour_metrics.time_leak_rate > 40 ? 'Time wasted' : 'Efficient'}
                    </p>
                  </div>

                  {/* Attempt Discipline */}
                  <div className="p-4 rounded-xl bg-slate-50 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Discipline</span>
                      <Target className={`w-4 h-4 ${report.behaviour_metrics.attempt_discipline >= 60 ? 'text-emerald-500' : 'text-amber-500'}`} />
                    </div>
                    <p className="text-2xl font-bold">{report.behaviour_metrics.attempt_discipline}%</p>
                    <p className="text-xs text-slate-500">
                      {report.behaviour_metrics.attempt_discipline >= 60 ? 'Consistent' : 'Variable'}
                    </p>
                  </div>

                  {/* Accuracy Stability */}
                  <div className="p-4 rounded-xl bg-slate-50 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-600">Stability</span>
                      <BarChart3 className={`w-4 h-4 ${report.behaviour_metrics.accuracy_stability >= 60 ? 'text-emerald-500' : 'text-amber-500'}`} />
                    </div>
                    <p className="text-2xl font-bold">{report.behaviour_metrics.accuracy_stability}%</p>
                    <p className="text-xs text-slate-500">
                      {report.behaviour_metrics.accuracy_stability >= 60 ? 'Stable scores' : 'Inconsistent'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Clearance Prediction */}
            {report.clearance_prediction && (
              <Card className="border-0 shadow-sm bg-gradient-to-br from-indigo-50 to-purple-50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Clearance Prediction</h3>
                      <p className="text-sm text-slate-500">Based on your behaviour patterns</p>
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-3">
                    <div className="p-4 rounded-xl bg-white/80 text-center">
                      <p className="text-3xl font-bold text-indigo-600">
                        {report.clearance_prediction.prelims_probability}%
                      </p>
                      <p className="text-sm text-slate-600">Clearance Probability</p>
                    </div>
                    <div className="p-4 rounded-xl bg-white/80 text-center">
                      <p className="text-xl font-bold text-slate-900">
                        {report.clearance_prediction.confidence_level}
                      </p>
                      <p className="text-sm text-slate-600">Confidence Level</p>
                    </div>
                    <div className="p-4 rounded-xl bg-white/80 text-center">
                      <p className="text-xl font-bold text-slate-900">
                        {report.clearance_prediction.estimated_score_range}
                      </p>
                      <p className="text-sm text-slate-600">Estimated Score Range</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recommendations */}
            {report.recommendations?.length > 0 && (
              <Card className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                      <Zap className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Personalized Recommendations</h3>
                      <p className="text-sm text-slate-500">Based on your behaviour analysis</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {report.recommendations.map((rec, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50">
                        <ChevronRight className="w-5 h-5 text-indigo-500 shrink-0 mt-0.5" />
                        <p className="text-sm text-slate-700">{rec}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Trends */}
            {report.trends && (
              <Card className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">Progress Trends</h3>
                        <p className="text-sm text-slate-500">Your journey over time</p>
                      </div>
                    </div>
                    <Badge className={report.trends.improvement_rate >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                      {report.trends.improvement_rate >= 0 ? '+' : ''}{report.trends.improvement_rate}% improvement
                    </Badge>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    {/* Accuracy Trend */}
                    <div className="p-4 rounded-xl bg-slate-50">
                      <p className="text-sm font-medium text-slate-700 mb-3">Accuracy Trend</p>
                      <div className="flex items-end gap-1 h-20">
                        {report.trends.accuracy_trend?.slice(-10).map((point, idx) => (
                          <div 
                            key={idx}
                            className="flex-1 bg-indigo-500 rounded-t"
                            style={{ height: `${Math.max(10, point.score)}%` }}
                            title={`${point.date}: ${point.score}%`}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Safety Movement */}
                    <div className="p-4 rounded-xl bg-slate-50">
                      <p className="text-sm font-medium text-slate-700 mb-3">Safety Zone Movement</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        {report.trends.safety_movement?.slice(-10).map((point, idx) => (
                          <Badge 
                            key={idx}
                            className={`text-xs bg-gradient-to-r ${getSafetyZoneColor(point.status)} text-white border-0`}
                            title={point.date}
                          >
                            {point.status[0].toUpperCase()}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        ) : null}
        
        {/* Show message for PRO users with no test data yet */}
        {isPro && !report.behaviour_metrics && (
          <Card className="border-0 shadow-sm bg-gradient-to-r from-indigo-50 to-purple-50">
            <CardContent className="p-8 text-center">
              <Sparkles className="w-12 h-12 text-indigo-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">PRO Features Unlocked!</h3>
              <p className="text-slate-600 mb-4">
                Take some tests to see your detailed behaviour analysis, clearance predictions, and personalized recommendations.
              </p>
              <Button onClick={() => navigate("/prelims/test-series")} className="bg-indigo-500 hover:bg-indigo-600">
                Start a Test <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* FREE User Upgrade CTA */}
        {!isPro && (
          <Card className="border-0 shadow-sm bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-lg">
                    <Lock className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">Unlock Full Progress Report</h3>
                    <p className="text-slate-600 mt-1">
                      PRO users see behaviour trends, clearance predictions, and personalized recommendations
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Badge variant="outline" className="text-xs">Behaviour Analysis</Badge>
                      <Badge variant="outline" className="text-xs">Multi-test Trends</Badge>
                      <Badge variant="outline" className="text-xs">Clearance Prediction</Badge>
                      <Badge variant="outline" className="text-xs">Personalized Tips</Badge>
                    </div>
                  </div>
                </div>
                <Button 
                  className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white shadow-lg px-8"
                  onClick={() => navigate("/upgrade")}
                >
                  Upgrade to PRO <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Positioning Statement */}
        <div className="text-center py-8 text-slate-500 text-sm">
          <p>&quot;Marks don&apos;t fail aspirants. Behaviour does.&quot;</p>
          <p className="mt-1">This report tracks how close you are to clearing — not just your scores.</p>
        </div>
      </div>
    </div>
  );
};

export default ProgressReportPage;
